import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import * as moment from 'moment';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { filter, map, mapTo, pluck, startWith, switchMap, take } from 'rxjs/operators';
import { Frac, FracClusterTruckingVendor } from '~lmo/models/frac.model';
import {
  FracClusterTruckingVendorRequest,
  TruckingVendor,
  TruckingVendorContract,
} from '~lmo/models/trucking-vendor.model';
import { driverPluralMapping } from '~lmo/pluralMappings';
import { LmoFracsService, LmoTruckingVendorsService } from '~lmo/services';
import { fctvSorter, LmoFracFctvsService } from '~lmo/services/lmo-frac-fctvs.service';
import { LmoFracModalService } from '~lmo/services/lmo-frac-modal.service';

interface AddNewFormValue {
  contract: TruckingVendorContract;
  dates: [Date, Date];
  description: string;
  distributions: { allocationPercent: number; fracClusterTruckingVendorId }[];
  vendor: TruckingVendor;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-fctv-add',
  styleUrls: ['./lmo-frac-fctv-add.component.scss'],
  templateUrl: './lmo-frac-fctv-add.component.html',
})
export class LmoFracFctvAddComponent implements OnInit {
  public driverPluralMapping = driverPluralMapping;
  @ViewChild('addVendor', { static: true }) private addVendor: TemplateRef<any>;
  public contractForm: FormControl = new FormControl();
  public contractManagement$$ = new BehaviorSubject<boolean>(false);
  public addNewForm: FormGroup;
  public frac$: Observable<Frac>;
  public fctvs$: Observable<FracClusterTruckingVendor[]>;
  public vendors$: Observable<{ name: string; data: TruckingVendor[] }[]>;
  public vendorContracts$: Observable<TruckingVendorContract[]>;
  public fctvIdsToVendorName$: Observable<Record<number, string>>;
  public maxDate = moment()
    .add(30, 'days')
    .toDate();
  public get distributions(): FormArray {
    return this.addNewForm.get('distributions') as FormArray;
  }

  public get addNewFormValue(): AddNewFormValue {
    return this.addNewForm.value;
  }
  constructor(
    private fb: FormBuilder,
    private fracService: LmoFracsService,
    private fracFctvsService: LmoFracFctvsService,
    private truckingVendorService: LmoTruckingVendorsService,
    private matDialog: MatDialog,
    private modalService: LmoFracModalService,
  ) {}

  public ngOnInit() {
    this.frac$ = this.fracService.currentFrac$;
    this.fctvs$ = this.fracFctvsService.currentFracFctvs$.pipe(
      map((fctvs) => (fctvs ? fctvs.filter((fctv) => fctv.status === 'awarded' || fctv.status === 'pending') : [])),
    );
    this.vendors$ = combineLatest([
      this.fctvs$.pipe(map((fctvs) => fctvs.map((fctv) => fctv.truckingVendorId))),
      this.truckingVendorService.inNetwork$,
      this.truckingVendorService.outOfNetwork$,
    ]).pipe(
      map(([fctvTruckingVendorIds, inNetwork, outOfNetwork]) => [
        {
          data: inNetwork.filter((vendor) => !fctvTruckingVendorIds.includes(vendor.id)),
          name: 'In Network',
        },
        {
          data: outOfNetwork,
          name: 'Out Of Network',
        },
      ]),
    );
    this.vendorContracts$ = this.truckingVendorService.vendorContracts$;
    this.fctvIdsToVendorName$ = this.fctvs$.pipe(
      map((fctvs) =>
        fctvs.reduce((record, fctv) => {
          record[fctv.id] = fctv.truckingVendorName;
          return record;
        }, {}),
      ),
    );
    this.setupForm();
    this.setupVendorListeners();
    this.setupContractDropDownListeners();
    this.setupContractListener();
  }

  public addFctv() {
    if (this.addNewForm.invalid) {
      return;
    }
    this.frac$
      .pipe(
        take(1),
        switchMap((frac) => {
          const formValue = this.addNewFormValue;
          const allocationPercents: {
            fracClusterTruckingVendorId: number;
            allocationPercent: number;
          }[] = formValue.distributions.map((distribution) => ({
            allocationPercent: distribution.allocationPercent,
            fracClusterTruckingVendorId: distribution.fracClusterTruckingVendorId,
          }));

          const fracClusterTruckingVendor: FracClusterTruckingVendorRequest = {
            description: formValue.description,
            endTimestamp: formValue.dates[1].toISOString(),
            fracClusterId: frac.cluster.id,
            startTimestamp: formValue.dates[0].toISOString(),
            status: 'pending',
            truckingVendorId: formValue.vendor.id,
            vendorContractId: formValue.contract.id,
          };
          return this.fracFctvsService.createFctv$({ allocationPercents, fracClusterTruckingVendor });
        }),
      )
      .subscribe((successfull: boolean) => {
        if (successfull) {
          this.modalService.closeHomeModal();
        }
      });
  }

  public getVendorNameFromId$(id: number): Observable<string> {
    if (id === 0) {
      return this.addNewForm.valueChanges.pipe(
        startWith(this.addNewFormValue),
        map((value: AddNewFormValue) => {
          if (value && value.vendor) {
            return value.vendor.name;
          }
          return null;
        }),
      );
    }
    return this.fctvIdsToVendorName$.pipe(pluck(`${id}`));
  }

  public vendorDisplayFn(vendor?: TruckingVendor | TruckingVendorContract): string | undefined {
    return vendor ? vendor.name : undefined;
  }

  public createNewContract() {
    this.contractForm.setValue(null);
    this.contractManagement$$.next(true);
  }

  public cancelContractChoice() {
    this.contractForm.setValue(null);
    this.contractManagement$$.next(false);
  }

  private addDistributionPercentage(vendorId: number, percentage: number) {
    this.distributions.insert(
      0,
      this.fb.group({
        allocationPercent: [percentage, [Validators.required, Validators.min(0), Validators.max(100)]],
        fracClusterTruckingVendorId: [vendorId, [Validators.required]],
      }),
    );
  }

  private setupForm() {
    this.addNewForm = this.fb.group({
      contract: [null, [Validators.required]],
      dates: [null, [Validators.required]],
      description: [null, []],
      distributions: this.fb.array([]),
      vendor: [null, [Validators.required]],
    });

    this.fctvs$
      .pipe(
        filter((fctvs) => !!fctvs),
        take(1),
      )
      .subscribe((fctvs) => {
        const sortedFctvs = fctvSorter(fctvs).reverse();
        sortedFctvs.forEach((fctv) => {
          this.addDistributionPercentage(fctv.id, fctv.allocationPercent);
        });
      });
  }

  private setupVendorListeners() {
    this.addNewForm
      .get('vendor')
      .valueChanges.pipe(
        filter((vendor) => !!vendor),
        switchMap((vendor: TruckingVendor) => {
          if (vendor.inNetwork) {
            return of(vendor);
          }
          return this.promptAddToNetworkOrRemove$(vendor);
        }),
        filter((vendor) => !!vendor),
      )
      .subscribe((truckingVendor: TruckingVendor) => {
        this.addNewForm.get('contract').setValue(null);
        if (
          this.addNewFormValue &&
          this.addNewFormValue.distributions.length &&
          this.addNewFormValue.distributions[0].fracClusterTruckingVendorId === 0
        ) {
          this.distributions.removeAt(0);
        }
        if (this.addNewFormValue.distributions.length === 0) {
          this.addDistributionPercentage(0, 100);
        } else {
          this.addDistributionPercentage(0, 0);
        }
        this.frac$
          .pipe(
            filter((frac) => !!frac),
            take(1),
          )
          .subscribe((frac) => {
            this.truckingVendorService.loadPossibleContractsFor(truckingVendor.id, frac.id);
          });
      });
  }

  private setupContractDropDownListeners() {
    this.contractForm.valueChanges.pipe(filter((contract) => !!contract)).subscribe((contract) => {
      this.contractManagement$$.next(true);
    });
  }

  private setupContractListener() {
    this.addNewForm
      .get('contract')
      .valueChanges.pipe(filter((contract) => !!contract))
      .subscribe((value) => {
        this.contractManagement$$.next(false);
        this.contractForm.setValue(value, { emitEvent: false });
      });
  }

  private promptAddToNetworkOrRemove$(vendor: TruckingVendor): Observable<TruckingVendor> {
    const dialog = this.matDialog.open(this.addVendor, {
      maxWidth: '968px',
      minWidth: '300px',
      restoreFocus: false,
      width: '30%',
    });
    return dialog.afterClosed().pipe(
      switchMap((value: false | 'add' | 'request-contact') => {
        if (!value) {
          this.addNewForm.get('vendor').setValue(null, { emitEvent: false });
          if (this.addNewFormValue && this.addNewFormValue.distributions[0].fracClusterTruckingVendorId === 0) {
            this.distributions.removeAt(0);
          }
          return of(null);
        }
        if (value === 'add') {
          return this.truckingVendorService.addVendorToNetwork$(vendor.id).pipe(mapTo(vendor));
        }

        if (value === 'request-contact') {
          this.addNewForm.get('vendor').setValue(null, { emitEvent: false });
          if (this.addNewFormValue && this.addNewFormValue.distributions[0].fracClusterTruckingVendorId === 0) {
            this.distributions.removeAt(0);
          }
          return this.truckingVendorService.requestContact$(vendor.id).pipe(mapTo(null));
        }
      }),
    );
  }
}
